import { useMemo, useState } from "react";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import PageHeader from "@/components/feature/PageHeader";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import { newsItems } from "@/mocks/content";

const PAGE_SIZE = 4;

function formatMonth(value: string) {
  const [year, month] = value.split("-");
  return `${year} 年 ${month} 月`;
}

export default function NewsCategory() {
  useDocumentMeta({
    title: "我的朋友他的妻子 - 最新资讯",
    description: "追踪该剧相关新闻与官方发布动态。",
    keywords: "我的朋友他的妻子,最新动态,资讯,新闻,官方发布",
    canonical: "https://zhengkangjixie.cn/category/news",
  });

  const [keyword, setKeyword] = useState("");
  const [month, setMonth] = useState("全部");
  const [page, setPage] = useState(1);

  const monthOptions = useMemo(() => {
    const set = new Set(newsItems.map((item) => item.date.slice(0, 7)));
    return ["全部", ...Array.from(set).sort((a, b) => b.localeCompare(a))];
  }, []);

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return newsItems
      .filter((item) => (month === "全部" ? true : item.date.startsWith(month)))
      .filter((item) => (key ? item.title.toLowerCase().includes(key) : true))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [keyword, month]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleKeyword = (value: string) => {
    setKeyword(value);
    setPage(1);
  };
  const handleMonth = (value: string) => {
    setMonth(value);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title="最新动态"
        subtitle="追踪《我的朋友他的妻子》的官方发布、收视表现与幕后资讯，第一时间了解剧集相关消息。"
        crumbs={[{ label: "首页", to: "/" }, { label: "最新动态" }]}
      />

      <div className="mx-auto w-full max-w-content px-4 py-8 md:px-6 md:py-12">
        <div className="mb-8 flex flex-col gap-4 rounded-lg border border-background-300 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(e) => handleKeyword(e.target.value)}
              placeholder="搜索新闻标题关键词"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <label className="flex shrink-0 items-center gap-2 text-sm text-foreground-600">
            <i className="ri-calendar-line"></i>
            <select
              value={month}
              onChange={(e) => handleMonth(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
            >
              {monthOptions.map((option) => (
                <option key={option} value={option}>
                  {option === "全部" ? "全部时间" : formatMonth(option)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {paged.length === 0 ? (
          <EmptyState message="暂无相关新闻" icon="ri-newspaper-line" />
        ) : (
          <>
            <p className="mb-4 text-sm text-foreground-500">
              共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 条相关动态
            </p>
            <div className="flex flex-col gap-4">
              {paged.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </>
  );
}